/**
 * PracticingSubTabs Component
 * Sub-tab navigation within the Practicing tab: Active, Upcoming, Completed, Draft.
 * Delegates to existing data hooks for ritual fetching and renders 2-col RitualCard grid.
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { RitualCard } from "@/components/cards/ritualCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { useUserRitualPractices } from "@/features/profile/hooks/useUserRitualPractices"
import { formatRitualType } from "@/features/rituals/types"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { PracticingSubTab } from "../types/my-rituality.types"
import { TabEmptyState } from "./TabEmptyState"

interface PracticingSubTabsProps {
  userId: string
  searchQuery?: string
}

export function PracticingSubTabs({ userId, searchQuery }: PracticingSubTabsProps) {
  const { t } = useTranslation("myRituality")
  const [activeSubTab, setActiveSubTab] = useState<PracticingSubTab>("active")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)
  const toggleSave = useToggleRitualSave()

  const { data, isLoading, isError } = useUserRitualPractices(userId, {
    limit: 50,
    search: debouncedSearch || undefined,
    subTab: activeSubTab,
  })

  const subTabs: { key: PracticingSubTab; label: string }[] = [
    { key: "active", label: t("practicingSubTabs.active") },
    { key: "upcoming", label: t("practicingSubTabs.upcoming") },
    { key: "completed", label: t("practicingSubTabs.completed") },
  ]

  // Transform API data to match RitualCardProps
  const rituals =
    data?.rituals.map(ritual => ({
      id: ritual.id,
      image: ritual.uploads?.find(u => u.isCover)?.filePath || undefined,
      userAvatar: ritual.createdBy?.coverImage || undefined,
      username: ritual.createdBy?.name || "Unknown",
      userHandle: ritual.createdBy?.username ? `@${ritual.createdBy.username}` : "",
      title: ritual.title,
      tags: [ritual.participationFee, formatRitualType(ritual.type)],
      duration: `${ritual.duration} min`,
      userId: ritual.createdBy?.id,
      isBookmarked: ritual.isSaved,
      onBookmarkClick: () => toggleSave.mutate(ritual.id),
    })) || []

  return (
    <div className="px-5 md:px-0">
      {/* Sub-tab header */}
      <div className="flex items-center justify-between">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-1.25 md:gap-3.75">
          <div className="flex justify-between items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              {t("tabs.practicing")}
            </div>
            <div className="md:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("tabs.search")}
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label={t("tabs.closeSearch")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label={t("tabs.search")}
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>

          {/* Vertical divider */}
          <div className="w-px h-7.5 bg-text-primary/10 hidden md:block" />

          {/* Sub-tabs */}
          <div className="flex items-center h-7.5 gap-3.75 md:gap-5 text-sm text-white overflow-x-auto scrollbar-hide">
            {subTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveSubTab(tab.key)}
                className={cn(
                  "text-sm cursor-pointer transition-opacity whitespace-nowrap shrink-0",
                  activeSubTab === tab.key ? "font-semibold opacity-100" : "font-normal opacity-70",
                )}
              >
                {tab.label} ({data?.counts?.[tab.key] ?? 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-primary my-5 md:mt-3 md:mb-2">
        {t("practicingSubTabs.description")}
      </p>

      {/* Content */}
      {(() => {
        const totalCount = Object.values(data?.counts ?? {}).reduce((sum, c) => sum + c, 0)

        if (isLoading) {
          return (
            <div className="flex justify-center py-12">
              <p className="text-sm text-primary/50">{t("practicingSubTabs.loading")}</p>
            </div>
          )
        }

        if (isError) {
          return (
            <div className="flex justify-center py-12">
              <p className="text-sm text-red-400">{t("practicingSubTabs.error")}</p>
            </div>
          )
        }

        if (rituals.length === 0 && debouncedSearch) {
          return (
            <div className="pt-8">
              <p className="text-sm text-primary/50">{t("practicingSubTabs.empty")}</p>
            </div>
          )
        }

        if (rituals.length === 0 && totalCount === 0) {
          return (
            <TabEmptyState
              title={t("practicingSubTabs.emptyTitle")}
              description={t("practicingSubTabs.emptyDescription")}
              ctaLabel={t("practicingSubTabs.exploreRituals")}
              ctaTo="/learn-and-practice"
            />
          )
        }

        if (rituals.length === 0) {
          return (
            <div className="flex justify-center py-12">
              <p className="text-sm text-primary/50">{t("practicingSubTabs.empty")}</p>
            </div>
          )
        }

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {rituals.map(ritual => (
              <div key={ritual.id} className="h-[260px] md:h-[440px]">
                <RitualCard {...ritual} />
              </div>
            ))}
          </div>
        )
      })()}
    </div>
  )
}
